import { useFormikContext } from 'formik';
import { useMemo } from 'react';

type AnyObject = Record<string, any>;

/**
 * Deep equality check for primitives, arrays, and plain objects.
 */
function deepEqual(a: any, b: any): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a && b && typeof a === 'object') {
    // Array?
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      return a.every((v, i) => deepEqual(v, b[i]));
    }
    // Plain object?
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every(key => deepEqual(a[key], b[key]));
  }
  return false;
}

/**
 * Flattens a nested object into a map of dot-path → value.
 * Recurses into arrays as well, producing paths like "field[0].subfield".
 */
function flattenObject(obj: AnyObject, prefix = ''): AnyObject {
  const out: AnyObject = {};
  for (const [key, val] of Object.entries(obj || {})) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      Object.assign(out, flattenObject(val, path));
    } else if (Array.isArray(val)) {
      val.forEach((item, i) =>
        Object.assign(out, flattenObject(item, `${path}[${i}]`))
      );
    } else {
      out[path] = val;
    }
  }
  return out;
}

/**
 * Returns an array of dot-paths where current ≠ initial, excluding any
 * paths that exactly match or end with one of the ignorePaths.
 * If `initialValues` is not initialized yet, uses `values` as baseline
 * so no false positives on first load.
 */
export function useDirtyPaths(ignorePaths: string[] = []): string[] {
  const { values, initialValues } = useFormikContext<AnyObject>();

  return useMemo(() => {
    const nowFlat = flattenObject(values);
    const baseInit = initialValues ?? values;
    const initFlat = flattenObject(baseInit);
    const allPaths = new Set<string>([
      ...Object.keys(nowFlat),
      ...Object.keys(initFlat),
    ]);

    return Array.from(allPaths).filter(path => {
      if (
        ignorePaths.some(
          ip =>
            path === ip || path.endsWith(`.${ip}`) || path.endsWith(`[${ip}]`)
        )
      ) {
        return false;
      }
      return !deepEqual(nowFlat[path], initFlat[path]);
    });
  }, [values, initialValues, ignorePaths]);
}

/**
 * Boolean: are there any dirty fields outside the ignored ones?
 */
export function useIsDirtyExcluding(ignorePaths: string[] = []): boolean {
  const dirtyPaths = useDirtyPaths(ignorePaths);
  return dirtyPaths.length > 0;
}
